import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { tasksApi, workflowsApi, usersApi, extractList, extractOne } from '../../api/facades';

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const TaskCreatePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [workflow_id, setWorkflowId] = useState<number | ''>('');
  const [task_subject, setTaskSubject] = useState('');
  const [order, setOrder] = useState<number | ''>('');
  const [depends_on_task_id, setDependsOnTaskId] = useState<number | ''>('');
  const [project_status, setProjectStatus] = useState('Pending');
  const [priority, setPriority] = useState('Medium');
  const [start_date, setStartDate] = useState('');
  const [end_date, setEndDate] = useState('');
  const [description, setDescription] = useState('');
  const [user_ids, setUserIds] = useState<number[]>([]);

  useEffect(() => {
    const state = (location.state ?? {}) as any;
    if (state?.date && typeof state.date === 'string') setStartDate(state.date);
  }, []);

  const { data: workflowsRes } = useQuery({ queryKey: ['workflows'], queryFn: () => workflowsApi.list({ per_page: 100 }) });
  const workflows = extractList(workflowsRes);

  const { data: workflowDetailRes } = useQuery({
    queryKey: ['workflows', workflow_id],
    queryFn: () => workflowsApi.get(workflow_id as number),
    enabled: typeof workflow_id === 'number',
  });
  const workflowDetail = extractOne(workflowDetailRes);
  const workflowTasks = (workflowDetail as any)?.tasks ?? [];

  const { data: usersRes } = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list({ per_page: 100 }) });
  const apiUsers = extractList(usersRes);

  const create = useMutation({
    mutationFn: () =>
      tasksApi.create({
        workflow_id: workflow_id === '' ? undefined : workflow_id,
        order: order === '' ? undefined : order,
        depends_on_task_id: depends_on_task_id === '' ? null : depends_on_task_id,
        task_subject: task_subject.trim(),
        project_status: project_status || null,
        priority: priority || null,
        start_date: start_date || null,
        end_date: end_date || null,
        description: description.trim() || null,
        user_ids: user_ids.length ? user_ids : undefined,
      }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      toast.success('Task created.');
      const task = data?.data?.data ?? data?.data;
      const wfId = task?.workflow_id;
      if (wfId) navigate(`/dashboard/workflows/${wfId}`);
      else if (task?.id) navigate(`/dashboard/tasks/${task.id}`);
      else navigate('/dashboard/tasks');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || e?.message || 'Create failed.'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!task_subject.trim()) return;
    create.mutate();
  };

  const toggleUser = (id: number) => {
    setUserIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <h1 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-4">New task</h1>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
        <FormField label="Workflow" hint="Optional">
          <select className={inputClass} value={workflow_id === '' ? '' : workflow_id} onChange={(e) => { setWorkflowId(e.target.value === '' ? '' : Number(e.target.value)); setDependsOnTaskId(''); }}>
            <option value="">— None (standalone) —</option>
            {workflows.map((w: any) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Task name" hint="Required">
          <input className={inputClass} value={task_subject} onChange={(e) => setTaskSubject(e.target.value)} required placeholder="e.g. Review contract terms" />
        </FormField>
        <FormField label="Order">
          <input className={inputClass} type="number" min={0} value={order === '' ? '' : order} onChange={(e) => setOrder(e.target.value === '' ? '' : parseInt(e.target.value, 10))} />
        </FormField>
        <FormField label="Depends on task">
          <select className={inputClass} value={depends_on_task_id === '' ? '' : depends_on_task_id} onChange={(e) => setDependsOnTaskId(e.target.value === '' ? '' : Number(e.target.value))} disabled={!workflow_id}>
            <option value="">— None —</option>
            {workflowTasks.map((t: any) => (
              <option key={t.id} value={t.id}>{t.task_subject} (#{t.id})</option>
            ))}
          </select>
        </FormField>
        <FormField label="Status">
          <select className={inputClass} value={project_status} onChange={(e) => setProjectStatus(e.target.value)}>
            <option value="Pending">Pending</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
          </select>
        </FormField>
        <FormField label="Priority">
          <select className={inputClass} value={priority} onChange={(e) => setPriority(e.target.value)}>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
          </select>
        </FormField>
        <FormField label="Start date">
          <input className={inputClass} type="date" value={start_date} onChange={(e) => setStartDate(e.target.value)} />
        </FormField>
        <FormField label="End date">
          <input className={inputClass} type="date" value={end_date} onChange={(e) => setEndDate(e.target.value)} />
        </FormField>
        <div className="md:col-span-2">
          <FormField label="Assignees">
            <div className="flex flex-wrap gap-2">
              {apiUsers.map((u: any) => (
                <label key={u.id} className="inline-flex items-center gap-1 text-sm">
                  <input type="checkbox" checked={user_ids.includes(u.id)} onChange={() => toggleUser(u.id)} />
                  <span>{u.name ?? u.email ?? u.id}</span>
                </label>
              ))}
            </div>
          </FormField>
        </div>
        <div className="md:col-span-2">
          <FormField label="Description">
            <textarea className={inputClass} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </FormField>
        </div>
        <div className="md:col-span-2 flex gap-2">
          <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!task_subject.trim() || create.isPending}>{create.isPending ? 'Creating…' : 'Create'}</Button>
        </div>
      </form>
    </div>
  );
};

export default TaskCreatePage;
